import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Subscription, interval } from 'rxjs';
import { config } from '../config';
import { AuthService, UserRole } from '../auth/auth.service';
import { RCOversightService } from '../services/rc-oversight.service';
import { RCDataSyncService } from '../services/rc-data-sync.service';
import { WebSocketService } from '../auth/web-socket.service';

interface Interview {
  _id: string;
  title: string;
  candidate_name: string;
  candidate_email: string;
  candidate_phone?: string;
  scheduled_date: string;
  duration: number;
  location?: string;
  status: 'SCHEDULED' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED' | 'RESCHEDULED';
  description?: string;
  interviewer_id: {
    _id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  contact_id: {
    _id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  company?: {
    id: string;
    name: string;
  };
  created_at: string;
  updated_at: string;
  isRC?: boolean; // For RC's own interviews
  isMC?: boolean; // For supervised MC interviews
}

interface InterviewStats {
  total: number;
  scheduled: number;
  confirmed: number;
  completed: number;
  cancelled: number;
  rescheduled: number;
  upcoming_today: number;
  upcoming_week: number;
}

@Component({
  selector: 'app-interviews',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="interviews-container">
      <div class="header">
        <div class="header-title">
          <h2>Interviews</h2>
        </div>
        <div class="header-actions">
          <button class="refresh-btn" (click)="manualRefresh()" [disabled]="loading">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            Refresh
          </button>
          <button class="schedule-btn">
            <span>+</span> Schedule
          </button>
        </div>
      </div>

      <!-- Statistics Cards -->
      <div class="stats-grid" *ngIf="stats">
        <div class="stat-card">
          <div class="stat-label">TOTAL</div>
          <div class="stat-number total">{{ stats.total }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">TODAY</div>
          <div class="stat-number today">{{ stats.upcoming_today }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">THIS WEEK</div>
          <div class="stat-number week">{{ stats.upcoming_week }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">SCHEDULED</div>
          <div class="stat-number scheduled">{{ stats.scheduled }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">CONFIRMED</div>
          <div class="stat-number confirmed">{{ stats.confirmed }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">COMPLETED</div>
          <div class="stat-number completed">{{ stats.completed }}</div>
        </div>
      </div>

      <!-- Filters -->
      <div class="filters">
        <div class="search-box">
          <svg class="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            type="text"
            [(ngModel)]="searchTerm"
            (input)="onSearch()"
            placeholder="Search candidates..."
            class="search-input">
        </div>

        <div class="filter-pill">
          <select [(ngModel)]="selectedStatus" (change)="onFilterChange()" class="pill-select">
            <option value="">Status: All</option>
            <option value="SCHEDULED">Status: Scheduled</option>
            <option value="CONFIRMED">Status: Confirmed</option>
            <option value="COMPLETED">Status: Completed</option>
            <option value="CANCELLED">Status: Cancelled</option>
            <option value="RESCHEDULED">Status: Rescheduled</option>
          </select>
          <svg class="pill-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </div>

        <div class="filter-pill">
          <select [(ngModel)]="selectedDateRange" (change)="onDateRangeChange()" class="pill-select">
            <option value="">Range: All Dates</option>
            <option value="today">Range: Today</option>
            <option value="tomorrow">Range: Tomorrow</option>
            <option value="week">Range: This Week</option>
            <option value="month">Range: This Month</option>
            <option value="custom">Range: Custom</option>
          </select>
          <svg class="pill-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </div>

        <div class="custom-date-group" *ngIf="selectedDateRange === 'custom'">
          <input type="date" [(ngModel)]="dateFrom" (change)="onFilterChange()" class="date-input">
          <span class="date-sep">–</span>
          <input type="date" [(ngModel)]="dateTo" (change)="onFilterChange()" class="date-input">
        </div>
      </div>

      <!-- Interview List -->
      <div class="interviews-list" *ngIf="!loading">
        <div class="interview-card" *ngFor="let interview of interviews" [class]="'status-' + interview.status.toLowerCase()">
          <div class="interview-header">
            <div class="interview-title">
              <h3>{{ interview.title }}</h3>
              <div class="badge-group">
                <span class="status-badge" [class]="'status-' + interview.status.toLowerCase()">
                  {{ interview.status }}
                </span>
                <span *ngIf="isRCInterview(interview)" class="company-badge rc-badge">RC</span>
                <span *ngIf="isMCInterview(interview)" class="company-badge mc-badge">MC</span>
              </div>
            </div> 
          
            <div class="interview-actions">
              <button 
                class="action-btn" 
                (click)="updateStatus(interview._id, 'CONFIRMED')"
                *ngIf="interview.status === 'SCHEDULED'">
                Confirm
              </button>
              <button 
                class="action-btn" 
                (click)="updateStatus(interview._id, 'COMPLETED')"
                *ngIf="interview.status === 'CONFIRMED'">
                Mark Complete
              </button>
              <button 
                class="action-btn cancel-btn" 
                (click)="updateStatus(interview._id, 'CANCELLED')"
                *ngIf="interview.status !== 'CANCELLED' && interview.status !== 'COMPLETED'">
                Cancel
              </button>
            </div>
          </div>
          
          <div class="interview-details">
            <div class="detail-row">
              <strong>Candidate:</strong> 
              {{ interview.candidate_name }} ({{ interview.candidate_email }})
              <span *ngIf="interview.candidate_phone"> • {{ interview.candidate_phone }}</span>
            </div>
            
            <div class="detail-row">
              <strong>Date & Time:</strong> 
              {{ formatDateTime(interview.scheduled_date) }} 
              ({{ interview.duration }} minutes)
            </div>
            
            <div class="detail-row" *ngIf="interview.location">
              <strong>Location:</strong> {{ interview.location }}
            </div>
            
            <div class="detail-row">
              <strong>Interviewer:</strong> 
              <span *ngIf="interview.interviewer_id?.first_name; else noInterviewer">
                {{ interview.interviewer_id.first_name }} {{ interview.interviewer_id.last_name }}
              </span>
              <ng-template #noInterviewer>
                <span class="text-muted">Not assigned</span>
              </ng-template>
            </div>
            
            <div class="detail-row" *ngIf="interview.company && isRCUser()">
              <strong>MC Company:</strong> 
              <span class="mc-company-tag">{{ interview.company.name }}</span>
            </div>
            
            <div class="detail-row" *ngIf="interview.description">
              <strong>Notes:</strong> {{ interview.description }}
            </div>
          </div>
        </div>

        <div class="empty-state" *ngIf="interviews.length === 0">
          <p>No interviews found matching your criteria.</p>
        </div>
      </div>

      <!-- Loading State -->
      <div class="loading-state" *ngIf="loading">
        <span class="ap-spinner"></span>
        <p>Loading interviews...</p>
      </div>

      <!-- Pagination -->
      <div class="pagination" *ngIf="totalPages > 1">
        <button 
          class="page-btn" 
          (click)="goToPage(currentPage - 1)"
          [disabled]="currentPage === 1">
          Previous
        </button>
        
        <span class="page-info">
          Page {{ currentPage }} of {{ totalPages }} ({{ totalInterviews }} total)
        </span>
        
        <button 
          class="page-btn" 
          (click)="goToPage(currentPage + 1)"
          [disabled]="currentPage === totalPages">
          Next
        </button>
      </div>
    </div>
  `,
  styles: [`
    .interviews-container {
      padding: 10px;
      width: 100%;
      box-sizing: border-box;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 20px;
      flex-wrap: wrap;
      gap: 10px;
    }

    .header-title h2 {
      margin: 0 0 2px 0;
      font-size: 1.35rem;
      font-weight: 700;
      color: var(--ap-text, #0B1220);
    }

    .header-actions {
      display: flex;
      gap: 8px;
      align-items: center;
      flex-wrap: wrap;
    }

    .refresh-btn {
      display: flex;
      align-items: center;
      gap: 6px;
      background: transparent;
      color: var(--ap-text-soft, #4B5563);
      border: 1px solid var(--ap-border, #E3E6EB);
      height: 34px;
      padding: 0 14px;
      border-radius: var(--ap-radius-sm, 6px);
      cursor: pointer;
      font-size: 13px;
      font-weight: 500;
    }

    .refresh-btn:hover:not(:disabled) {
      background: var(--ap-surface-muted, #EEF0F3);
      color: var(--ap-text, #0B1220);
    }

    .refresh-btn:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .schedule-btn {
      display: flex;
      align-items: center;
      gap: 5px;
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
      border: none;
      height: 34px;
      padding: 0 16px;
      border-radius: var(--ap-radius-sm, 6px);
      cursor: pointer;
      font-size: 13px;
      font-weight: 600;
    }

    .schedule-btn:hover {
      background: #2449b8;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      gap: 12px;
      margin-bottom: 20px;
    }

    .stat-card {
      background: var(--ap-surface, #fff);
      border-radius: var(--ap-radius, 10px);
      padding: 14px 16px;
      border: 1px solid var(--ap-border, #E3E6EB);
    }

    .stat-label {
      font-size: 10.5px;
      font-weight: 600;
      color: var(--ap-text-muted, #6B7280);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-bottom: 6px;
    }

    .stat-number {
      font-size: 28px;
      font-weight: 700;
      line-height: 1;
    }

    .stat-number.total   { color: var(--ap-text, #0B1220); }
    .stat-number.today   { color: var(--ap-text, #0B1220); }
    .stat-number.week    { color: var(--ap-primary, #2F5BDA); }
    .stat-number.scheduled { color: var(--ap-warn, #B45309); }
    .stat-number.confirmed { color: var(--ap-success, #047A55); }
    .stat-number.completed { color: var(--ap-success, #047A55); }

    /* Filters row */
    .filters {
      display: flex;
      gap: 10px;
      margin-bottom: 20px;
      flex-wrap: wrap;
      align-items: center;
    }

    /* Search box */
    .search-box {
      position: relative;
      display: flex;
      align-items: center;
    }

    .search-icon {
      position: absolute;
      left: 10px;
      color: var(--ap-text-muted, #6B7280);
      pointer-events: none;
    }

    .search-input {
      padding: 7px 12px 7px 30px;
      border: 1px solid var(--ap-border, #E3E6EB);
      border-radius: var(--ap-radius-sm, 6px);
      font-size: 13px;
      width: 220px;
      background: var(--ap-surface, #fff);
      color: var(--ap-text, #0B1220);
      height: 34px;
      box-sizing: border-box;
    }

    .search-input:focus {
      outline: none;
      border-color: var(--ap-primary, #2F5BDA);
      box-shadow: var(--ap-ring, 0 0 0 3px rgba(47,91,218,.18));
    }

    /* Pill selects */
    .filter-pill {
      position: relative;
      display: flex;
      align-items: center;
    }

    .pill-select {
      appearance: none;
      -webkit-appearance: none;
      padding: 0 30px 0 12px;
      height: 34px;
      border: 1px solid var(--ap-border, #E3E6EB);
      border-radius: var(--ap-radius-sm, 6px);
      font-size: 13px;
      font-weight: 500;
      background: var(--ap-surface, #fff);
      color: var(--ap-text-soft, #4B5563);
      cursor: pointer;
    }

    .pill-select:focus {
      outline: none;
      border-color: var(--ap-primary, #2F5BDA);
    }

    .pill-chevron {
      position: absolute;
      right: 9px;
      color: var(--ap-text-muted, #6B7280);
      pointer-events: none;
    }

    /* Custom date inputs */
    .custom-date-group {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .date-input {
      padding: 0 10px;
      height: 34px;
      border: 1px solid var(--ap-border, #E3E6EB);
      border-radius: var(--ap-radius-sm, 6px);
      font-size: 13px;
      background: var(--ap-surface, #fff);
      color: var(--ap-text, #0B1220);
    }

    .date-sep {
      color: var(--ap-text-muted, #6B7280);
      font-size: 13px;
    }

    .interviews-list {
      display: flex;
      flex-direction: column;
      gap: 15px;
    }

    .interview-card {
      background: var(--ap-surface, #fff);
      border-radius: var(--ap-radius, 10px);
      padding: 20px;
      border: 1px solid var(--ap-border, #E3E6EB);
      border-left: 4px solid var(--ap-primary, #2F5BDA);
    }

    .interview-card.status-scheduled { border-left-color: var(--ap-warn, #B45309); }
    .interview-card.status-confirmed { border-left-color: var(--ap-success, #047A55); }
    .interview-card.status-completed { border-left-color: var(--ap-text-muted, #6B7280); }
    .interview-card.status-cancelled { border-left-color: var(--ap-danger, #B42318); }
    .interview-card.status-rescheduled { border-left-color: var(--ap-primary, #2F5BDA); }

    .interview-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
      flex-wrap: wrap;
      gap: 10px;
    }

    .interview-title {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
    }

    .interview-title h3 {
      margin: 0;
      color: var(--ap-text, #0B1220);
    }

    .status-badge {
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: bold;
      text-transform: uppercase;
    }

    .status-badge.status-scheduled { background: var(--ap-warn-soft, #FEF3C7); color: var(--ap-warn, #B45309); }
    .status-badge.status-confirmed { background: var(--ap-success-soft, #D1FAE5); color: var(--ap-success, #047A55); }
    .status-badge.status-completed { background: var(--ap-surface-muted, #EEF0F3); color: var(--ap-text-soft, #4B5563); }
    .status-badge.status-cancelled { background: var(--ap-danger-soft, #FEE2E2); color: var(--ap-danger, #B42318); }
    .status-badge.status-rescheduled { background: var(--ap-primary-soft, #E8EEFC); color: var(--ap-primary, #2F5BDA); }

    .badge-group {
      display: flex;
      gap: 8px;
      align-items: center;
      flex-wrap: wrap;
    }

    .company-badge {
      padding: 3px 8px;
      border-radius: 10px;
      font-size: 10px;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .rc-badge {
      background: var(--ap-primary-soft, #E8EEFC);
      color: var(--ap-primary, #2F5BDA);
      border: 1px solid var(--ap-primary, #2F5BDA);
    }

    .mc-badge {
      background: var(--ap-warn-soft, #FEF3C7);
      color: var(--ap-warn, #B45309);
      border: 1px solid var(--ap-warn, #B45309);
    }

    .interview-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    .action-btn {
      background: var(--ap-success-soft, #D1FAE5);
      color: var(--ap-success, #047A55);
      border: 1px solid var(--ap-success-soft, #D1FAE5);
      padding: 0 12px;
      height: 28px;
      border-radius: var(--ap-radius-sm, 6px);
      cursor: pointer;
      font-size: 12px;
      font-weight: 550;
    }

    .action-btn:hover:not(:disabled) {
      background: var(--ap-success, #047A55);
      color: #fff;
    }

    .action-btn.cancel-btn {
      background: var(--ap-danger-soft, #FEE2E2);
      color: var(--ap-danger, #B42318);
      border-color: var(--ap-danger-soft, #FEE2E2);
    }

    .action-btn.cancel-btn:hover {
      background: var(--ap-danger, #B42318);
      color: #fff;
    }

    .interview-details {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .detail-row {
      font-size: 14px;
      color: var(--ap-text-soft, #4B5563);
    }

    .detail-row strong {
      color: var(--ap-text, #0B1220);
      margin-right: 8px;
    }

    .text-muted {
      color: var(--ap-text-muted, #6B7280);
      font-style: italic;
    }

    .mc-company-tag {
      background: var(--ap-primary-soft, #E8EEFC);
      color: var(--ap-primary, #2F5BDA);
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .empty-state,
    .loading-state {
      text-align: center;
      padding: 40px;
      color: var(--ap-text-muted, #6B7280);
    }

    .pagination {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 15px;
      margin-top: 20px;
    }

    .page-btn {
      background: var(--ap-primary-soft, #E8EEFC);
      color: var(--ap-primary, #2F5BDA);
      border: 1px solid var(--ap-primary-soft, #E8EEFC);
      height: 32px;
      padding: 0 14px;
      border-radius: var(--ap-radius-sm, 6px);
      cursor: pointer;
      font-size: 13px;
      font-weight: 550;
    }

    .page-btn:hover:not(:disabled) {
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
    }

    .page-btn:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .page-info {
      color: var(--ap-text-muted, #6B7280);
      font-size: 14px;
    }

    /* Responsive Design */
    @media (max-width: 1024px) {
      .stats-grid {
        grid-template-columns: repeat(3, 1fr);
      }
    }

    @media (max-width: 768px) {
      .header {
        flex-direction: column;
        align-items: stretch;
      }

      .header-actions {
        justify-content: flex-start;
        flex-wrap: wrap;
        gap: 8px;
      }

      .search-input {
        width: 100%;
        max-width: 240px;
      }

      .stats-grid {
        grid-template-columns: repeat(2, 1fr);
      }

      .stat-number {
        font-size: 22px;
      }

      .filters {
        gap: 8px;
      }

      .interview-header {
        flex-direction: column;
        align-items: stretch;
      }

      .interview-actions {
        justify-content: center;
      }

      .pagination {
        gap: 10px;
      }

      .page-btn {
        padding: 7px 12px;
        font-size: 13px;
      }

      .page-info {
        font-size: 13px;
      }
    }

    @media (max-width: 480px) {
      .stats-grid {
        grid-template-columns: repeat(2, 1fr);
      }

      .stat-number {
        font-size: 20px;
      }

      .stat-label {
        font-size: 10px;
      }

      .search-input {
        width: 160px;
      }

      .interview-card {
        padding: 15px;
      }

      .interview-title {
        flex-direction: column;
        align-items: flex-start;
      }

      .pagination {
        flex-wrap: wrap;
        gap: 8px;
        justify-content: center;
      }

      .page-btn {
        padding: 6px 10px;
        font-size: 12px;
      }

      .page-info {
        width: 100%;
        text-align: center;
        font-size: 12px;
        order: -1;
      }
    }
  `]
})
export class InterviewsComponent implements OnInit, OnDestroy {
  private apiUrl = config.apiUrl;
  private subscriptions = new Subscription();
  
  // Data
  interviews: Interview[] = [];
  stats: InterviewStats | null = null;
  
  // State
  loading = false;
  
  // UK timezone only (as requested)
  timezone: string = 'Europe/London';
  
  // Pagination
  currentPage = 1;
  totalPages = 1;
  totalInterviews = 0;
  pageSize = 10;
  
  // Filters
  searchTerm = '';
  selectedStatus = '';
  selectedDateRange = '';
  dateFrom = '';
  dateTo = '';
  
  // Smart refresh configuration
  private refreshInterval = 300000; // 5 minutes fallback (reduced from 30s)
  private smartRefreshEnabled = true; // Feature flag for rollback
  private lastDataRefresh = 0;
  private MIN_REFRESH_INTERVAL = 5000; // Minimum 5s between refreshes
  
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private rcOversightService: RCOversightService,
    private rcDataSyncService: RCDataSyncService,
    private wsService: WebSocketService
  ) {}
  
  ngOnInit() {
    this.loadInterviews();
    this.loadStats();
    
    // Smart refresh implementation
    this.setupSmartRefresh();
  }

  private setupSmartRefresh(): void {
    if (this.smartRefreshEnabled) {
      console.log('[InterviewsComponent] Setting up smart refresh with WebSocket events');
      this.setupWebSocketRefresh();
      this.setupFallbackPolling();
    } else {
      console.log('[InterviewsComponent] Using legacy 30-second polling');
      this.setupLegacyPolling();
    }
  }

  private setupWebSocketRefresh(): void {
    // Smart refresh: Only refresh when data actually changes
    try {
      if (this.isRCSuperAdmin()) {
        console.log('[InterviewsComponent] Setting up RC WebSocket refresh');
        this.subscriptions.add(
          this.rcDataSyncService.interviewUpdates$.subscribe({
            next: (event) => {
              console.log('[InterviewsComponent] RC data sync event received:', event);
              this.handleSmartRefresh(event, 'RC WebSocket event');
            },
            error: (error) => {
              console.error('[InterviewsComponent] RC WebSocket error:', error);
              // Don't fail - fallback polling will continue working
            }
          })
        );
      }
      
      // General WebSocket events for all users (RC and non-RC)
      console.log('[InterviewsComponent] Setting up general WebSocket event subscription');
      this.subscriptions.add(
        this.wsService.messages$.subscribe({
          next: (message) => {
            if (message.type === 'interview_change') {
              // Check if this event affects current user's company
              const currentUser = this.authService.currentUserValue;
              if (currentUser && message['company_id'] === currentUser.company_id) {
                console.log('[InterviewsComponent] General WebSocket event received:', message);
                this.handleSmartRefresh(message, 'General WebSocket event');
              }
            }
          },
          error: (error) => {
            console.error('[InterviewsComponent] General WebSocket error:', error);
            // Don't fail - fallback polling will continue working
          }
        })
      );
    } catch (error) {
      console.error('[InterviewsComponent] Error setting up WebSocket refresh:', error);
      // Continue with fallback polling if WebSocket setup fails
    }
  }

  private setupFallbackPolling(): void {
    // Fallback polling: Every 5 minutes as safety net (instead of 30s)
    console.log('[InterviewsComponent] Setting up fallback polling every 5 minutes');
    this.subscriptions.add(
      interval(this.refreshInterval).subscribe(() => {
        console.log('[InterviewsComponent] Fallback refresh triggered after 5 minutes');
        this.performRefresh('Fallback polling');
      })
    );
  }

  private setupLegacyPolling(): void {
    // Legacy 30-second polling for rollback
    this.subscriptions.add(
      interval(30000).subscribe(() => {
        this.performRefresh('Legacy polling');
      })
    );
  }

  private handleSmartRefresh(event: any, source: string): void {
    if (event.event === 'created' || event.event === 'updated' || event.event === 'deleted') {
      const now = Date.now();
      
      // Debounce: Don't refresh too frequently
      if (now - this.lastDataRefresh < this.MIN_REFRESH_INTERVAL) {
        console.log(`[InterviewsComponent] Skipping refresh from ${source} - too recent (${now - this.lastDataRefresh}ms ago)`);
        return;
      }
      
      this.lastDataRefresh = now;
      console.log(`[InterviewsComponent] Smart refresh triggered by ${source} for event: ${event.event}`);
      this.performRefresh(source);
    }
  }

  private performRefresh(source: string): void {
    console.log(`[InterviewsComponent] Performing refresh from: ${source}`);
    this.loadInterviews();
    this.loadStats();
  }

  // Public method for manual refresh (maintains existing functionality)
  public manualRefresh(): void {
    console.log('[InterviewsComponent] Manual refresh triggered');
    this.performRefresh('Manual refresh');
  }

  // Method to toggle smart refresh (for debugging/rollback)
  public toggleSmartRefresh(enabled: boolean): void {
    console.log(`[InterviewsComponent] Toggling smart refresh: ${enabled}`);
    this.smartRefreshEnabled = enabled;
    
    // Restart refresh setup
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.setupSmartRefresh();
  }
  
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
  
  loadInterviews() {
    this.loading = true;
    
    const currentUser = this.authService.currentUserValue;
    console.log('[Interviews] Current user role:', currentUser?.role);
    console.log('[Interviews] Is RC User:', this.isRCUser());
    
    const params: any = {
      page: this.currentPage,
      limit: this.pageSize
    };
    
    if (this.searchTerm) params.search_term = this.searchTerm;
    if (this.selectedStatus) params.status = this.selectedStatus;
    if (this.dateFrom) params.date_from = this.dateFrom;
    if (this.dateTo) params.date_to = this.dateTo;
    
    // Check if user is ACTUALLY a super admin (RC user)
    if (currentUser?.role === UserRole.OWNER && this.isRCUser()) {
      console.log('[Interviews] Loading both RC and MC interviews for RC user...');
      this.loadRCAndMCInterviews(params);
    } else {
      console.log('[Interviews] Loading regular interviews...');
      this.loadRegularInterviews(params);
    }
  }

  private loadRCAndMCInterviews(params: any): void {
    const currentUser = this.authService.currentUserValue;
    const rcCompanyId = currentUser?.company_id;

    // Backend returns RC + MC interviews combined via RC data synchronization
    this.http.get<any>(`${config.apiUrl}/api/interviews`, { params }).subscribe({
      next: (response) => {
        if (response && response.success && response.data) {
          const interviews = response.data.interviews || [];

          // Tag each interview as RC or MC based on company_id
          this.interviews = interviews.map((interview: any) => ({
            ...interview,
            isRC: interview.company_id === rcCompanyId,
            isMC: interview.company_id !== rcCompanyId
          })).sort((a: any, b: any) => new Date(b.scheduled_date).getTime() - new Date(a.scheduled_date).getTime());

          this.totalInterviews = response.data.total || this.interviews.length;
          this.currentPage = response.data.page;
          this.totalPages = response.data.totalPages || Math.ceil(this.totalInterviews / this.pageSize);
          console.log('[Interviews] Loaded RC+MC interviews:', this.interviews.length, 'of', this.totalInterviews);
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('[Interviews] Failed to load interviews:', err);
        this.interviews = [];
        this.loading = false;
      }
    });
  }

  private loadRegularInterviews(params: any): void {
    // Load all interviews for other roles (existing logic)
    const queryString = new URLSearchParams(params).toString();
    
    this.http.get<any>(`${this.apiUrl}/api/interviews?${queryString}`).subscribe({
      next: (response) => {
        if (response.success) {
          let interviews = response.data.interviews;
          
          // Backend already filters interviews by user's company for USER role
          console.log('[Interviews] Loaded interviews from backend:', interviews.length);
          
          this.interviews = interviews;
          this.totalInterviews = response.data.total || interviews.length;
          this.currentPage = response.data.page;
          this.totalPages = response.data.totalPages || Math.ceil(this.totalInterviews / this.pageSize);
          console.log('[Interviews] Loaded regular interviews:', this.interviews.length, 'of', this.totalInterviews);
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading interviews:', error);
        this.loading = false;
      }
    });
  }
  
  loadStats() {
    // Use regular stats endpoint for all roles — backend handles RC data synchronization
    console.log('[Interviews] Loading interview stats...');
    this.loadRegularStats();
  }

  private loadRegularStats(): void {
    // Load interview stats for all roles - backend handles filtering
    this.http.get<any>(`${this.apiUrl}/api/interviews/stats`).subscribe({
      next: (response) => {
        if (response.success) {
          this.stats = response.data;
          console.log('[Interviews] Loaded interview stats');
        }
      },
      error: (error) => {
        console.error('Error loading interview stats:', error);
      }
    });
  }
  
  onSearch() {
    this.currentPage = 1;
    this.loadInterviews();
  }
  
  onFilterChange() {
    this.currentPage = 1;
    this.loadInterviews();
  }
  
  onDateRangeChange() {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    switch (this.selectedDateRange) {
      case 'today':
        this.dateFrom = today.toISOString().split('T')[0];
        this.dateTo = today.toISOString().split('T')[0];
        break;
      case 'tomorrow':
        this.dateFrom = tomorrow.toISOString().split('T')[0];
        this.dateTo = tomorrow.toISOString().split('T')[0];
        break;
      case 'week':
        this.dateFrom = today.toISOString().split('T')[0];
        const weekEnd = new Date(today);
        weekEnd.setDate(weekEnd.getDate() + 7);
        this.dateTo = weekEnd.toISOString().split('T')[0];
        break;
      case 'month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        this.dateFrom = monthStart.toISOString().split('T')[0];
        this.dateTo = monthEnd.toISOString().split('T')[0];
        break;
      case '':
        this.dateFrom = '';
        this.dateTo = '';
        break;
    }
    
    if (this.selectedDateRange !== 'custom') {
      this.onFilterChange();
    }
  }
  
  updateStatus(interviewId: string, newStatus: string) {
    this.http.patch<any>(`${this.apiUrl}/api/interviews/${interviewId}/status`, {
      status: newStatus
    }).subscribe({
      next: (response) => {
        if (response.success) {
          // Update the local interview status
          const interview = this.interviews.find(i => i._id === interviewId);
          if (interview) {
            interview.status = newStatus as any;
          }
          // Refresh stats
          this.loadStats();
        }
      },
      error: (error) => {
        console.error('Error updating interview status:', error);
        alert('Failed to update interview status. Please try again.');
      }
    });
  }
  
  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadInterviews();
    }
  }
  
  formatDateTime(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleString('en-GB', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Europe/London'
    });
  }

  isRCUser(): boolean {
    const currentUser = this.authService.currentUserValue;
    return currentUser?.role === UserRole.OWNER && 
           currentUser?.company_type === 'RC';
  }

  isRCSuperAdmin(): boolean {
    return this.isRCUser();
  }

  // Check if interview belongs to RC company
  isRCInterview(interview: any): boolean {
    return interview.isRC === true;
  }

  // Check if interview belongs to MC company  
  isMCInterview(interview: any): boolean {
    return interview.isMC === true;
  }

  // Calculate stats from filtered interviews for USER role
  private calculateStatsFromInterviews(interviews: Interview[]): InterviewStats {
    const today = new Date();
    const weekFromNow = new Date();
    weekFromNow.setDate(today.getDate() + 7);
    
    return {
      total: interviews.length,
      scheduled: interviews.filter(i => i.status === 'SCHEDULED').length,
      confirmed: interviews.filter(i => i.status === 'CONFIRMED').length,
      completed: interviews.filter(i => i.status === 'COMPLETED').length,
      cancelled: interviews.filter(i => i.status === 'CANCELLED').length,
      rescheduled: interviews.filter(i => i.status === 'RESCHEDULED').length,
      upcoming_today: interviews.filter(i => {
        const interviewDate = new Date(i.scheduled_date);
        return interviewDate.toDateString() === today.toDateString() && 
               (i.status === 'SCHEDULED' || i.status === 'CONFIRMED');
      }).length,
      upcoming_week: interviews.filter(i => {
        const interviewDate = new Date(i.scheduled_date);
        return interviewDate >= today && interviewDate <= weekFromNow && 
               (i.status === 'SCHEDULED' || i.status === 'CONFIRMED');
      }).length
    };
  }
}